import type {
  HalfEdgeOffsets,
  HalfEdgeToWallEndCapSource,
  WallEndCap,
  WallEndCapSource,
  HalfEdgeTangents,
  WallEndMiterJoin,
  WallEndButtJoin,
  FreeEndWallCapSource,
  NoOpWallCapSource,
  MiterWallEndCapSource,
  SolidWallEndCapSource,
  HalfEdgeToMiters,
} from "building-types/walls/wallEndsTypes";
import { ButtCapMode, EndCapSourceType } from "building-types/walls/wallEndsTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallPathBase } from "building-types/walls/wallTypes";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { DIST_EPSILON } from "geom/core";
import { line2Eval, line2Tangent } from "geom/line2";
import { polyLine2VariableMiterPt } from "geom/offset";
import type { PlanarGraph, PlanarGraphFacePoleOfInaccessibilityPositions } from "geom/planarGraph";
import {
  planarGraphHalfEdgeEnd,
  planarGraphHalfEdgeLength,
  planarGraphHalfEdgeNormal,
  planarGraphHalfEdgeStart,
  planarGraphHalfEdgeStartVertex,
  planarGraphHalfEdgeTangent,
  planarGraphHalfEdgeToLine2,
} from "geom/planarGraph";
import type { Vec2 } from "geom/vec2";
import { vec2Dot, vec2Normalize, vec2NormsCollinear, vec2Perp, vec2Scale, vec2Sub } from "geom/vec2";
import {
  repairFaceMitersOnSelfIntersectingRoomEdges,
  repairMitersOnSelfIntersectingRoomEdges,
} from "./wallSystemRepairIncorrectMiters";

export function halfEdgeOffsetsNew(leftOffset: number, rightOffset: number): HalfEdgeOffsets {
  return {
    leftOffset,
    rightOffset,
  };
}

export function halfEdgeOffsetsExtend(info: HalfEdgeOffsets, leftOffset: number, rightOffset: number): HalfEdgeOffsets {
  return {
    leftOffset: info.leftOffset + leftOffset,
    rightOffset: info.rightOffset + rightOffset,
  };
}

export function halfEdgeOffsetsOffset(info: HalfEdgeOffsets, offset: number): HalfEdgeOffsets {
  return {
    leftOffset: info.leftOffset - offset,
    rightOffset: info.rightOffset + offset,
  };
}

export function halfEdgeOffsetsOpposite(info: HalfEdgeOffsets): HalfEdgeOffsets {
  return {
    leftOffset: info.rightOffset,
    rightOffset: info.leftOffset,
  };
}

export function halfEdgeTangentsNew(ltx: number, lty: number, rtx: number, rty: number): HalfEdgeTangents {
  return {
    leftTangentX: ltx,
    leftTangentY: lty,
    rightTangentX: rtx,
    rightTangentY: rty,
  };
}

export function wallEndCapNew(
  verts: number[],
  isFreeEnd: boolean,
  offsetDifferenceOutline: number[] | undefined,
  rightMiterExtend: boolean | undefined,
  leftOffset: number,
  rightOffset: number,
  leftTangentX: number,
  leftTangentY: number,
  rightTangentX: number,
  rightTangentY: number,
): WallEndCap {
  return {
    verts,
    isFreeEnd,
    offsetDifferenceOutline,
    rightMiterExtend,
    leftOffset,
    rightOffset,
    leftTangentX,
    leftTangentY,
    rightTangentX,
    rightTangentY,
  };
}

export function makeWallEndCaps({
  planarGraph,
  planarGraphFacePoleOfInaccessibilityPositions,
  wallEndCapSources,
  useRepairMitersOnSelfIntersectingRoomEdges = false,
}: {
  planarGraph: PlanarGraph;
  planarGraphFacePoleOfInaccessibilityPositions?: PlanarGraphFacePoleOfInaccessibilityPositions;
  wallEndCapSources: HalfEdgeToWallEndCapSource;
  useRepairMitersOnSelfIntersectingRoomEdges?: boolean;
}): WallEndCap[] {
  if (useRepairMitersOnSelfIntersectingRoomEdges) {
    repairMitersOnSelfIntersectingRoomEdges(
      wallEndCapSources,
      planarGraph,
      planarGraphFacePoleOfInaccessibilityPositions,
    );
  }

  // const halfEdgeToMiters: HalfEdgeToMiters = []; // enable for debug
  const wallEndCaps = makeWallEndCapsFromWallEndCapSources(wallEndCapSources /*halfEdgeToMiters*/);

  // TODO: useful code for QC'ing miters
  //debugDisplayMitersAndExtensions(wallEndCaps, halfEdgeToMiters, rawPlanarGraph);

  return wallEndCaps;
}

export function makeFaceWallEndCaps({
  face,
  planarGraph,
  wallEndCapSources,
  facePoleOfInaccessibilityPositionForRepair,
}: {
  face: number;
  planarGraph: PlanarGraph;
  facePoleOfInaccessibilityPositionForRepair?: Vec2;
  wallEndCapSources: HalfEdgeToWallEndCapSource;
}): WallEndCap[] {
  if (facePoleOfInaccessibilityPositionForRepair) {
    repairFaceMitersOnSelfIntersectingRoomEdges(
      face,
      wallEndCapSources,
      planarGraph,
      facePoleOfInaccessibilityPositionForRepair,
    );
  }

  // const halfEdgeToMiters: HalfEdgeToMiters = []; // enable for debug
  const wallEndCaps = makeWallEndCapsFromWallEndCapSources(wallEndCapSources /*halfEdgeToMiters*/);

  // TODO: useful code for QC'ing miters
  //debugDisplayMitersAndExtensions(wallEndCaps, halfEdgeToMiters, rawPlanarGraph);

  return wallEndCaps;
}

interface WallInfoProvider<T extends WallPathBase> {
  /**
   * @returns the WallPathBase information for this halfEdge.
   * The wallType of SOLID_MASS/SOLID/VOID will determine the WallEndCap
   * types of this and adjacent walls
   */
  getWallPath(halfEdge: number): T;
  /**
   * @returns the HalfEdgeOffsets used for determining the left and right
   * distance for the miters at this half edge
   */
  getWallEndCapOffsets(halfEdge: number): HalfEdgeOffsets;
  /**
   * @returns the HalfEdgeOffsets used for determining how far to offset
   * any wall that is the colinear portion of a T junction
   */
  getWallTJunctionOffsets(halfEdge: number): HalfEdgeOffsets;
  /**
   * @returns the WallEndMiterJoin that should be used in the event that this
   * endCap is a miter
   */
  getWallMiterJoin(halfEdge: number): WallEndMiterJoin;
  /**
   * @returns the WallEndMiterJoin that should be used in the event that this
   * endCap is a free end
   */
  getWallFreeEndJoin(halfEdge: number): WallEndButtJoin;
}

export function makeWallEndCapSources<PathT extends WallPathBase>(
  wallInfoProvider: WallInfoProvider<PathT>,
  planarGraph: PlanarGraph,
) {
  const { halfEdges } = planarGraph;

  const wallEndCapSources: HalfEdgeToWallEndCapSource = [];

  for (let i = 0, l = halfEdges.length; i < l; i += 2) {
    const halfEdge = i / 2;
    wallEndCapSources[halfEdge] = makeHalfEdgeWallEndCapSource(halfEdge, planarGraph, wallInfoProvider);
  }

  return wallEndCapSources;
}

export function makeVertexHalfEdgeWallEndCapSources<PathT extends WallPathBase>(
  wallInfoProvider: WallInfoProvider<PathT>,
  planarGraph: PlanarGraph,
  vertex: number,
) {
  const { vertexHalfEdges } = planarGraph;

  const vertexWallEndCapSources: WallEndCapSource[] = [];

  const curHalfEdges = vertexHalfEdges[vertex];
  for (let i = 0, heCount = curHalfEdges.length; i < heCount; i++) {
    vertexWallEndCapSources.push(makeHalfEdgeWallEndCapSource(curHalfEdges[i], planarGraph, wallInfoProvider));
  }

  return vertexWallEndCapSources;
}

function makeHalfEdgeWallEndCapSource<T extends WallPathBase>(
  halfEdge: number,
  planarGraph: PlanarGraph,
  wallInfoProvider: WallInfoProvider<T>,
): WallEndCapSource {
  const wallPath = wallInfoProvider.getWallPath(halfEdge);

  // if the rooms are connected, there's nothing to do
  if (wallPath.wallType === WallType.VOID) {
    return makeNoOpWallEndCapSource(halfEdge, planarGraph, wallInfoProvider);
  } else {
    return makeSolidWallEndCapSource(halfEdge, planarGraph, wallInfoProvider);
  }
}

function makeNoOpWallEndCapSource<T extends WallPathBase>(
  halfEdge: number,
  planarGraph: PlanarGraph,
  wallInfoProvider: WallInfoProvider<T>,
): NoOpWallCapSource {
  // find the left solid half edge by iterating CCW, skipping void walls
  const leftHalfEdge = getNextSolidHalfEdgeInRotationalDirection<T>(planarGraph, halfEdge, wallInfoProvider, false);

  // find the right solid half edge by iterating CW, skipping void walls
  const rightHalfEdge = getNextSolidHalfEdgeInRotationalDirection<T>(planarGraph, halfEdge, wallInfoProvider, true);

  const [sx, sy] = planarGraphHalfEdgeStart(planarGraph, halfEdge);

  // if there is a solid wall adjacent to this halfEdge
  if (leftHalfEdge !== null && rightHalfEdge !== null) {
    const { rightOffset: lrOffset } = wallInfoProvider.getWallEndCapOffsets(leftHalfEdge);
    const { leftOffset: rlOffset } = wallInfoProvider.getWallEndCapOffsets(rightHalfEdge);

    const [rex, rey] = planarGraphHalfEdgeEnd(planarGraph, rightHalfEdge);
    const [lex, ley] = planarGraphHalfEdgeEnd(planarGraph, leftHalfEdge);

    const [voidmx, voidmy] = polyLine2VariableMiterPt(rex, rey, sx, sy, lex, ley, lrOffset, rlOffset);

    return {
      halfEdge,
      sourceType: EndCapSourceType.NO_OP,
      sx: voidmx,
      sy: voidmy,
    };
  }

  return {
    halfEdge,
    sourceType: EndCapSourceType.NO_OP,
    sx,
    sy,
  };
}

function makeSolidWallEndCapSource<T extends WallPathBase>(
  halfEdge: number,
  planarGraph: PlanarGraph,
  wallInfoProvider: WallInfoProvider<T>,
): SolidWallEndCapSource {
  // find the left solid half edge by iterating CCW, skipping void walls
  const leftHalfEdge = getNextSolidHalfEdgeInRotationalDirection<T>(planarGraph, halfEdge, wallInfoProvider, false);

  // find the right solid half edge by iterating CW, skipping void walls
  const rightHalfEdge = getNextSolidHalfEdgeInRotationalDirection<T>(planarGraph, halfEdge, wallInfoProvider, true);

  // this is a free end. the only solid wall at this vertex is the halfEdge we are on
  if (leftHalfEdge === halfEdge) {
    return makeFreeEndWallEndCapSource(halfEdge, planarGraph, wallInfoProvider);
  }

  assertIsNotNullOrUndefined(leftHalfEdge);
  assertIsNotNullOrUndefined(rightHalfEdge);

  // this is a miter
  return makeMiterWallEndCapSource(halfEdge, planarGraph, leftHalfEdge, rightHalfEdge, wallInfoProvider);
}

function makeFreeEndWallEndCapSource<T extends WallPathBase>(
  halfEdge: number,
  planarGraph: PlanarGraph,
  wallInfoProvider: WallInfoProvider<T>,
): FreeEndWallCapSource {
  const [sx, sy] = planarGraphHalfEdgeStart(planarGraph, halfEdge);
  const [ex, ey] = planarGraphHalfEdgeEnd(planarGraph, halfEdge);

  const wallOffsets = wallInfoProvider.getWallEndCapOffsets(halfEdge);
  const wallFreeEndJoin = wallInfoProvider.getWallFreeEndJoin(halfEdge);

  switch (wallFreeEndJoin.buttCapMode) {
    case ButtCapMode.END_MITER_EXTEND: {
      const { buttCapDefault, buttCapMaxPerpEdgeAligned } = wallFreeEndJoin;
      const buttCapExtension = buttCapMaxPerpEdgeAligned
        ? getMaxPerpWallOffsetAtHalfEdge(planarGraph, halfEdge, wallInfoProvider, buttCapDefault)
        : wallOffsets.leftOffset; // TODO - consider more control of this ? (i.e Math.max(leftOffset, rightOffset))
      return makeEndMiterExtendFreeEndWallCapSource(sx, sy, ex, ey, halfEdge, buttCapExtension, wallOffsets);
    }
    case ButtCapMode.END_ALIGNED: {
      const { buttCapExtension } = wallFreeEndJoin;
      return makeEndAlignedFreeEndWallCapSource(sx, sy, ex, ey, halfEdge, buttCapExtension, wallOffsets);
    }
    default: {
      assertUnreachable(wallFreeEndJoin);
    }
  }
}

/**
 * @TODO - this needs to be broken up further
 */
function makeMiterWallEndCapSource<T extends WallPathBase>(
  halfEdge: number,
  planarGraph: PlanarGraph,
  leftHalfEdge: number,
  rightHalfEdge: number,
  wallInfoProvider: WallInfoProvider<T>,
): MiterWallEndCapSource {
  // imagine you are standing at a vertex looking along a half edge
  // we want to find the miter points, which are typically perp bisectors of
  // the half edges to the left and right of the half edge we are looking along
  // the vertex we are standing on and these miter points define the wall end cap

  const { leftOffset: leftWallOffset, rightOffset: rightWallOffset } = wallInfoProvider.getWallEndCapOffsets(halfEdge);

  // get the start vertex position
  const [sx, sy] = planarGraphHalfEdgeStart(planarGraph, halfEdge);
  const [ex, ey] = planarGraphHalfEdgeEnd(planarGraph, halfEdge);

  let [leftX, leftY] = planarGraphHalfEdgeEnd(planarGraph, leftHalfEdge);
  let [rightX, rightY] = planarGraphHalfEdgeEnd(planarGraph, rightHalfEdge);

  let [ltx, lty] = vec2Sub(leftX, leftY, sx, sy);
  [ltx, lty] = vec2Normalize(ltx, lty);
  let [rtx, rty] = vec2Sub(rightX, rightY, sx, sy);
  [rtx, rty] = vec2Normalize(rtx, rty);

  const [tx, ty] = planarGraphHalfEdgeTangent(planarGraph, halfEdge);
  const [nx, ny] = planarGraphHalfEdgeNormal(planarGraph, halfEdge);
  const [lnx, lny] = planarGraphHalfEdgeNormal(planarGraph, leftHalfEdge);
  const [rnx, rny] = planarGraphHalfEdgeNormal(planarGraph, rightHalfEdge);

  const leftWallPathCollinear = vec2NormsCollinear(nx, ny, lnx, lny);
  const rightWallPathCollinear = vec2NormsCollinear(nx, ny, rnx, rny);

  const { leftOffset: rightWallPathOffsetLeft } = wallInfoProvider.getWallEndCapOffsets(rightHalfEdge);
  const { rightOffset: leftWallPathOffsetRight } = wallInfoProvider.getWallEndCapOffsets(leftHalfEdge);

  // we need to normalize the left and right points for correct miters
  const halfEdgeLength = planarGraphHalfEdgeLength(planarGraph, halfEdge);
  [rightX, rightY] = line2Eval(sx, sy, rightX, rightY, halfEdgeLength, false);
  [leftX, leftY] = line2Eval(sx, sy, leftX, leftY, halfEdgeLength, false);

  const wallCornerJoin = wallInfoProvider.getWallMiterJoin(halfEdge);

  let leftAdjustedStartX = sx;
  let leftAdjustedStartY = sy;

  let rightAdjustedStartX = sx;
  let rightAdjustedStartY = sy;

  let sourceType: EndCapSourceType.DEFAULT | EndCapSourceType.LEFT_MITER_EXTEND | EndCapSourceType.RIGHT_MITER_EXTEND =
    EndCapSourceType.DEFAULT;

  if (rightWallPathCollinear && rightWallOffset !== rightWallPathOffsetLeft) {
    if (rightWallOffset > rightWallPathOffsetLeft) {
      sourceType = EndCapSourceType.RIGHT_MITER_EXTEND;
      if (leftHalfEdge !== rightHalfEdge) {
        if (wallCornerJoin.shiftTJunction) {
          const { leftOffset: leftTOffset } = wallInfoProvider.getWallTJunctionOffsets(leftHalfEdge);
          rightAdjustedStartX -= tx * leftTOffset;
          rightAdjustedStartY -= ty * leftTOffset;
        }
      }
    } else {
      sourceType = EndCapSourceType.DEFAULT; // receiving end of T offset
      if (leftHalfEdge !== rightHalfEdge) {
        const rightMiterJoin = wallInfoProvider.getWallMiterJoin(rightHalfEdge);
        if (rightMiterJoin.shiftTJunction) {
          const { rightOffset: rightTOffset } = wallInfoProvider.getWallTJunctionOffsets(leftHalfEdge);
          rightAdjustedStartX += tx * rightTOffset;
          rightAdjustedStartY += ty * rightTOffset;
        }
      }
    }
  } else if (leftWallPathCollinear && leftWallOffset !== leftWallPathOffsetRight) {
    if (leftWallOffset > leftWallPathOffsetRight) {
      sourceType = EndCapSourceType.LEFT_MITER_EXTEND;
      if (leftHalfEdge !== rightHalfEdge) {
        if (wallCornerJoin.shiftTJunction) {
          const { rightOffset: rightTOffset } = wallInfoProvider.getWallTJunctionOffsets(rightHalfEdge);
          leftAdjustedStartX -= tx * rightTOffset;
          leftAdjustedStartY -= ty * rightTOffset;
        }
      }
    } else {
      sourceType = EndCapSourceType.DEFAULT; // receiving end of T offset
      if (leftHalfEdge !== rightHalfEdge) {
        const leftMiterJoin = wallInfoProvider.getWallMiterJoin(leftHalfEdge);
        if (leftMiterJoin.shiftTJunction) {
          const { leftOffset: leftTOffset } = wallInfoProvider.getWallTJunctionOffsets(rightHalfEdge);
          leftAdjustedStartX += tx * leftTOffset;
          leftAdjustedStartY += ty * leftTOffset;
        }
      }
    }
  }

  const [lmx, lmy] = polyLine2VariableMiterPt(
    ex,
    ey,
    leftAdjustedStartX,
    leftAdjustedStartY,
    leftX,
    leftY,
    leftWallOffset,
    leftWallPathCollinear ? leftWallOffset : leftWallPathOffsetRight,
  );

  const [rmx, rmy] = polyLine2VariableMiterPt(
    rightX,
    rightY,
    rightAdjustedStartX,
    rightAdjustedStartY,
    ex,
    ey,
    rightWallPathCollinear ? rightWallOffset : rightWallPathOffsetLeft,
    rightWallOffset,
  );

  switch (sourceType) {
    case EndCapSourceType.RIGHT_MITER_EXTEND: {
      const [lx, ly] = polyLine2VariableMiterPt(
        ex,
        ey,
        rightAdjustedStartX,
        rightAdjustedStartY,
        rightX,
        rightY,
        rightWallPathOffsetLeft,
        rightWallPathOffsetLeft,
      );
      const [fillerlx, fillerly] = polyLine2VariableMiterPt(
        ex,
        ey,
        rightAdjustedStartX,
        rightAdjustedStartY,
        rightX,
        rightY,
        -rightWallPathOffsetLeft,
        rightWallPathOffsetLeft,
      );

      return {
        halfEdge,
        leftWallOffset,
        rightWallOffset,
        sourceType,
        lmx,
        lmy,
        sx,
        sy,
        lx,
        ly,
        fillerlx,
        fillerly,
        rmx,
        rmy,
        ltx,
        lty,
        rtx,
        rty,
      };
    }
    case EndCapSourceType.LEFT_MITER_EXTEND: {
      const [rx, ry] = polyLine2VariableMiterPt(
        ex,
        ey,
        leftAdjustedStartX,
        leftAdjustedStartY,
        leftX,
        leftY,
        leftWallPathOffsetRight,
        leftWallPathOffsetRight,
      );

      return {
        halfEdge,
        leftWallOffset,
        rightWallOffset,
        sourceType,
        lmx,
        lmy,
        rx,
        ry,
        sx,
        sy,
        rmx,
        rmy,
        ltx,
        lty,
        rtx,
        rty,
      };
    }
    case EndCapSourceType.DEFAULT: {
      // we don't need the start point if this is a corner or 2 colinear walls
      const vertex = planarGraphHalfEdgeStartVertex(planarGraph, halfEdge);
      const vertexHalfEdges = planarGraph.vertexHalfEdges[vertex];
      const vertexSolidWalls = vertexHalfEdges.filter(
        (he) => wallInfoProvider.getWallPath(he).wallType !== WallType.VOID,
      ).length;

      return {
        halfEdge,
        leftWallOffset,
        rightWallOffset,
        sourceType,
        lmx,
        lmy,
        sx,
        sy,
        rmx,
        rmy,
        omitStart: vertexSolidWalls <= 2,
        ltx,
        lty,
        rtx,
        rty,
      };
    }
    default: {
      assertUnreachable(sourceType);
    }
  }
}

function makeEndMiterExtendFreeEndWallCapSource(
  sx: number,
  sy: number,
  ex: number,
  ey: number,
  halfEdge: number,
  wallEndCapOffset: number,
  wallOffsets: HalfEdgeOffsets,
): FreeEndWallCapSource {
  const { leftOffset: leftWallOffset, rightOffset: rightWallOffset } = wallOffsets;
  const [dx, dy] = vec2Sub(ex, ey, sx, sy);
  const [tx, ty] = line2Tangent(sx, sy, ex, ey);

  const [lmx, lmy] = polyLine2VariableMiterPt(
    sx - dy,
    sy + dx,
    sx,
    sy,
    sx - dx,
    sy - dy,
    wallEndCapOffset,
    leftWallOffset,
  );
  const [rmx, rmy] = polyLine2VariableMiterPt(
    sx - dx,
    sy - dy,
    sx,
    sy,
    sx + dy,
    sy - dx,
    rightWallOffset,
    wallEndCapOffset,
  );

  return {
    halfEdge,
    leftWallOffset,
    rightWallOffset,
    sourceType: EndCapSourceType.FREE_END,
    lmx,
    lmy,
    sx,
    sy,
    rmx,
    rmy,
    ltx: tx,
    lty: ty,
    rtx: tx,
    rty: ty,
  };
}

function makeEndAlignedFreeEndWallCapSource(
  sx: number,
  sy: number,
  ex: number,
  ey: number,
  halfEdge: number,
  wallEndCapOffset: number,
  wallOffsets: HalfEdgeOffsets,
): FreeEndWallCapSource {
  const { leftOffset: leftWallOffset, rightOffset: rightWallOffset } = wallOffsets;
  const [tx, ty] = line2Tangent(sx, sy, ex, ey);

  const [nx, ny] = vec2Perp(tx, ty);

  const [lx, ly] = vec2Scale(nx, ny, leftWallOffset);
  const [rx, ry] = vec2Scale(nx, ny, rightWallOffset);
  const [dx, dy] = vec2Scale(tx, ty, wallEndCapOffset);

  const lmx = sx - lx + dx;
  const lmy = sy - ly + dy;

  const rmx = sx + rx + dx;
  const rmy = sy + ry + dy;

  return {
    halfEdge,
    leftWallOffset,
    rightWallOffset,
    sourceType: EndCapSourceType.FREE_END,
    lmx,
    lmy,
    sx,
    sy,
    rmx,
    rmy,
    ltx: tx,
    lty: ty,
    rtx: tx,
    rty: ty,
  };
}

function makeWallEndCapsFromWallEndCapSources(
  wallEndCapSources: WallEndCapSource[],
  wallEndCapMiterDebug?: HalfEdgeToMiters,
): WallEndCap[] {
  const halfEdgeToWallEnd: WallEndCap[] = [];
  for (const halfEdge of Object.keys(wallEndCapSources)) {
    const wallEndCapSource = wallEndCapSources[halfEdge] as WallEndCapSource;

    let verts: number[] | undefined;
    let filler: number[] | undefined;
    let rightMiterExtend: boolean | undefined;

    if (wallEndCapSource.sourceType === EndCapSourceType.NO_OP) {
      continue;
    }

    const { ltx: leftTangentX, lty: leftTangentY, rtx: rightTangentX, rty: rightTangentY } = wallEndCapSource;

    switch (wallEndCapSource.sourceType) {
      case EndCapSourceType.FREE_END: {
        const { lmx, lmy, rmx, rmy } = wallEndCapSource;
        verts = [lmx, lmy, rmx, rmy];
        break;
      }
      case EndCapSourceType.DEFAULT: {
        const { lmx, lmy, sx, sy, rmx, rmy, omitStart } = wallEndCapSource;
        verts = omitStart ? [lmx, lmy, rmx, rmy] : [lmx, lmy, sx, sy, rmx, rmy];
        break;
      }
      case EndCapSourceType.LEFT_MITER_EXTEND: {
        const { lmx, lmy, rx, ry, sx, sy, rmx, rmy } = wallEndCapSource;
        verts = [lmx, lmy, rx, ry, sx, sy, rmx, rmy];
        filler = [lmx, lmy, rx, ry];
        rightMiterExtend = false;
        break;
      }
      case EndCapSourceType.RIGHT_MITER_EXTEND: {
        const { lmx, lmy, sx, sy, lx, ly, rmx, rmy, fillerlx, fillerly } = wallEndCapSource;
        verts = [lmx, lmy, sx, sy, lx, ly, rmx, rmy];
        filler = [fillerlx, fillerly, rmx, rmy];
        rightMiterExtend = true;
        break;
      }
    }

    const { lmx, lmy, rmx, rmy, leftWallOffset, rightWallOffset, sourceType: miterType } = wallEndCapSource;

    if (wallEndCapMiterDebug) {
      wallEndCapMiterDebug[halfEdge] = {
        lmx,
        lmy,
        rmx,
        rmy,
      };
    }

    halfEdgeToWallEnd[halfEdge] = wallEndCapNew(
      verts,
      miterType === EndCapSourceType.FREE_END,
      filler,
      rightMiterExtend,
      leftWallOffset,
      rightWallOffset,
      leftTangentX,
      leftTangentY,
      rightTangentX,
      rightTangentY,
    );
  }

  return halfEdgeToWallEnd;
}

function getNextSolidHalfEdgeInRotationalDirection<PathT extends WallPathBase>(
  planarGraph: PlanarGraph,
  halfEdge: number,
  wallInfoProvider: WallInfoProvider<PathT>,
  clockwise: boolean,
): number | null {
  const vertex = planarGraphHalfEdgeStartVertex(planarGraph, halfEdge);
  const vertexHalfEdges = planarGraph.vertexHalfEdges[vertex];
  const heIndex = vertexHalfEdges.indexOf(halfEdge);
  if (!(heIndex >= 0)) {
    console.assert(false, "halfEdge not in planarGraph");
  }
  const heCount = vertexHalfEdges.length;

  let he: number | null = null;
  for (let k = 1; k < heCount + 1; k++) {
    const candHe = vertexHalfEdges[(clockwise ? heIndex - k + heCount : heIndex + k) % heCount];
    const candWallPath = wallInfoProvider.getWallPath(candHe);

    if (candWallPath.wallType === WallType.VOID) {
      continue;
    }

    he = candHe;
    break;
  }

  return he;
}

function getMaxPerpWallOffsetAtHalfEdge<PathT extends WallPathBase>(
  planarGraph: PlanarGraph,
  halfEdge: number,
  wallInfoProvider: WallInfoProvider<PathT>,
  minPerpOffset: number,
): number {
  const vertex = planarGraphHalfEdgeStartVertex(planarGraph, halfEdge);
  const vertexHalfEdges = planarGraph.vertexHalfEdges[vertex];
  const heIndex = vertexHalfEdges.indexOf(halfEdge);
  if (!(heIndex >= 0)) {
    console.assert(false, "halfEdge not in planarGraph");
  }
  const heCount = vertexHalfEdges.length;

  const { halfEdges, vertexPositions } = planarGraph;
  const [sx, sy, ex, ey] = planarGraphHalfEdgeToLine2(planarGraph, halfEdge);

  let maxPerpOffsetLength = minPerpOffset;
  for (let k = 1; k < heCount + 1; k++) {
    const canHalfEdge = vertexHalfEdges[(heIndex - k + heCount) % heCount];
    const candWallPath = wallInfoProvider.getWallPath(canHalfEdge);

    if (candWallPath.wallType !== WallType.VOID) {
      break;
    }

    const info = wallInfoProvider.getWallEndCapOffsets(candWallPath.halfEdgeIndex) ?? null;

    // if wall path info is invalid, can't proceed
    if (!info || Number.isNaN(info.leftOffset) || Number.isNaN(info.rightOffset)) {
      continue;
    }

    const candEndVertex = halfEdges[candWallPath.halfEdgeIndex * 2 + 1];
    const candEndX = vertexPositions[candEndVertex * 2];
    const candEndY = vertexPositions[candEndVertex * 2 + 1];

    const wallCandDot = Math.abs(vec2Dot(ex - sx, ey - sy, candEndX - sx, candEndY - sy));

    if (wallCandDot > DIST_EPSILON) {
      continue;
    }

    const wallCandNormalDot = vec2Dot(ey - sy, -(ex - sx), candEndX - sx, candEndY - sy);
    if (wallCandNormalDot > DIST_EPSILON) {
      if (info.rightOffset > maxPerpOffsetLength) {
        maxPerpOffsetLength = info.rightOffset;
      }
    } else if (wallCandNormalDot < DIST_EPSILON) {
      if (info.rightOffset > maxPerpOffsetLength) {
        maxPerpOffsetLength = info.leftOffset;
      }
    }
  }

  return maxPerpOffsetLength;
}
